import { useCallback, useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';

import { Link } from 'react-router-dom';
import { BRAND } from '@yuan-math/config';
import { DEFAULT_OFFER, MAX_CODES_PER_REQUEST, MAX_NOTE_LENGTH, describeOffer, parseOffer } from '@yuan-math/promo';
import { Badge, Button, Card, SectionHeading, Stat } from '@yuan-math/ui';
import { useAuth } from '../state/AuthContext.tsx';
import {
  ACCOUNTS_PER_PAGE,
  fetchAccounts,
  fetchOverview,
  mintPromoCodes,
  type AdminAccount,
  type AdminOverview,
  type MintedCodes,
} from '../state/admin.ts';
import { AccountsUnavailable } from './RegisterPage.tsx';

/**
 * The operator's page.
 *
 * Two jobs, and deliberately only two: see the state of the platform, and give
 * somebody the product for free. Everything else an operator might want is
 * already somewhere better — billing lives in Stripe's dashboard, and rows live
 * in Supabase's.
 *
 * ## What an administrator does not see
 *
 * No learner's attempts, mastery or misconceptions appear anywhere on this
 * page, and the database will not return them: `admin_accounts` is a fixed
 * projection and `learner_profiles.data` is not in it. The reasoning is the
 * teacher dashboard's, one level up — a student who believes every keystroke is
 * watched stops guessing, and guessing is how mathematics is learned (§14,
 * §29). Running the platform is not a reason to read a child's work.
 *
 * ## Why the gate below is not the gate
 *
 * The check here decides what to render and nothing more. The anon key ships in
 * the bundle, so the boundary that matters is in `database/admin-schema.sql`:
 * every query behind this page is a `security definer` function that raises for
 * a non-administrator. Someone who patches this component into rendering gets
 * an empty page.
 */
export function AdminPage(): JSX.Element {
  const { account, signedIn, configured, ready, isAdmin } = useAuth();

  if (!configured) return <AccountsUnavailable />;

  // Waiting for the session, or for the database's answer. Rendering the
  // refusal first would flash "this page is not for you" at an administrator on
  // every reload, which is the kind of thing that erodes trust in a page.
  if (!ready || isAdmin === null) {
    return <div className="route-loading" aria-busy="true" />;
  }

  if (!signedIn || !account) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">Sign in to continue</h1>
        <p className="page__lead">This page is for {BRAND.name} administrators.</p>
        <div className="button-row">
          <Link to="/signin" className="btn btn--primary">Sign in</Link>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">This page is not for you</h1>
        <p className="page__lead">
          You are signed in, but this account is not an administrator. Nothing is wrong — there is simply nothing
          here for you.
        </p>
        <div className="button-row">
          <Link to="/home" className="btn btn--primary">Back to my learning</Link>
          <Link to="/map" className="btn btn--secondary">Explore the Math Map</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--wide">
      <header className="page-header">
        <h1 className="page__title">Administration</h1>
        <p className="page__lead">
          The state of {BRAND.name}, and the one lever that gives somebody the product for nothing.
        </p>
      </header>

      <Overview />
      <PromoCodes />
      <Accounts />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The summary                                                                */
/* -------------------------------------------------------------------------- */

function Overview(): JSX.Element {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void fetchOverview().then((result) => {
      if (cancelled) return;
      setOverview(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <Card><p>Counting…</p></Card>;

  if (!overview) {
    return (
      <Card>
        <p>
          The summary could not be read. That usually means <code>database/admin-schema.sql</code> has not been
          applied to this project yet.
        </p>
      </Card>
    );
  }

  const { accounts, learners, subscriptions, classes } = overview;
  const paying = subscriptions
    .filter((row) => row.status === 'active' || row.status === 'trialing' || row.status === 'past_due')
    .reduce((sum, row) => sum + row.count, 0);

  return (
    <>
      <section className="dashboard-section">
        <SectionHeading
          title="Accounts"
          description="Signed up, and how far through getting started they are."
        />
        <div className="dashboard-stats dashboard-stats--wide">
          <Stat label="Accounts" value={accounts.total} />
          <Stat label="Active" value={accounts.active} hint="email confirmed" />
          <Stat label="Not yet confirmed" value={accounts.pending} />
          <Stat label="New this week" value={accounts.newLast7Days} hint={`${accounts.newLast30Days} in 30 days`} />
        </div>
        {accounts.awaitingConsent > 0 ? (
          <Card className="reteach-notice">
            <h2 className="consent-notice__title">
              {accounts.awaitingConsent} child {accounts.awaitingConsent === 1 ? 'account is' : 'accounts are'} waiting
              on a guardian
            </h2>
            <p>
              A child account cannot be taught until a guardian has approved it (§29). This is the one queue on this
              page that somebody can actually unblock — usually by re-sending the email.
            </p>
          </Card>
        ) : null}
      </section>

      <section className="dashboard-section">
        <SectionHeading
          title="Learning"
          description="Whether anybody is using this. Deliberately not what they are learning — that is between a student, their guardian and their teacher."
        />
        <div className="dashboard-stats dashboard-stats--wide">
          <Stat label="Learner profiles" value={learners.profiles} />
          <Stat label="Active this week" value={learners.activeLast7Days} hint={`${learners.activeLast30Days} in 30 days`} />
          <Stat label="Median XP" value={learners.medianXp} hint="a typical learner" />
          <Stat label="Classes" value={classes.total} hint={`${classes.students} students enrolled`} />
        </div>
      </section>

      <section className="dashboard-section">
        <SectionHeading title="Subscriptions" description="Passed straight through from Stripe, unaltered." />
        <div className="dashboard-stats dashboard-stats--wide">
          <Stat label="Entitled" value={paying} hint="active, trialing or past due" />
          <Stat label="Free accounts" value={Math.max(accounts.total - paying, 0)} />
        </div>
        {subscriptions.length === 0 ? (
          <Card><p>No subscriptions yet.</p></Card>
        ) : (
          <Card>
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">Plan</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="admin-table__number">Count</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((row) => (
                  <tr key={`${row.tier}-${row.status}`}>
                    <td>{row.tier}</td>
                    <td>
                      {/* `past_due` keeps full access here on purpose: an expired
                          card is a billing problem, not a reason to interrupt a
                          child's lesson. */}
                      <Badge tone={badgeToneFor(row.status)}>{row.status.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="admin-table__number">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>
    </>
  );
}

function badgeToneFor(status: string): 'correct' | 'warn' | 'incorrect' | 'neutral' {
  if (status === 'active' || status === 'trialing') return 'correct';
  if (status === 'past_due' || status === 'incomplete') return 'warn';
  if (status === 'canceled' || status === 'unpaid') return 'incorrect';
  return 'neutral';
}

/* -------------------------------------------------------------------------- */
/* Promotion codes                                                            */
/* -------------------------------------------------------------------------- */

type OfferForm = {
  count: string;
  percent: string;
  duration: string;
  tiers: string[];
  maxRedemptions: string;
  expires: string;
  prefix: string;
  note: string;
  firstTimeOnly: boolean;
};

const BLANK_FORM: OfferForm = {
  count: '1',
  percent: '100',
  duration: 'forever',
  tiers: [],
  maxRedemptions: '1',
  expires: '',
  prefix: DEFAULT_OFFER.prefix,
  note: '',
  firstTimeOnly: false,
};

/** The form's strings as the Edge Function wants them. */
const toPayload = (form: OfferForm) => ({
  count: Number(form.count),
  percent: Number(form.percent),
  duration: form.duration,
  tiers: form.tiers,
  maxRedemptions: Number(form.maxRedemptions),
  expires: form.expires,
  prefix: form.prefix,
  note: form.note,
  firstTimeOnly: form.firstTimeOnly,
});

/**
 * Minting comped subscriptions.
 *
 * A code is a real Stripe coupon, and redeeming one makes a real subscription
 * at $0 — the signed webhook grants entitlement exactly as it does for a paying
 * customer. There is no free-account table and no second way to be entitled;
 * `subscriptions` has no insert policy for anybody, including whoever is
 * reading this page.
 *
 * The offer is validated here *and* again in the Edge Function, by the same
 * function. The copy here is so the sentence under the form is honest before
 * anything is spent; the copy there is the one that counts.
 */
function PromoCodes(): JSX.Element {
  const [form, setForm] = useState<OfferForm>(BLANK_FORM);
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<MintedCodes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const set = <K extends keyof OfferForm>(key: K, value: OfferForm[K]): void =>
    setForm((current) => ({ ...current, [key]: value }));

  // The same parse the server runs, so the sentence below the form is the offer
  // that would actually be created — not a hopeful rendering of the inputs.
  const preview = useMemo(() => parseOffer(toPayload(form)), [form]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!preview.ok) {
      setError(preview.error);
      return;
    }
    setError(null);
    setBusy(true);
    setMinted(null);
    setCopied(false);

    const result = await mintPromoCodes(toPayload(form));
    setBusy(false);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    setMinted(result);
  };

  const copyAll = useCallback(async (): Promise<void> => {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(minted.codes.join('\n'));
      setCopied(true);
    } catch {
      // No clipboard permission. The codes are on screen and selectable, which
      // is the fallback that always works.
      setCopied(false);
    }
  }, [minted]);

  return (
    <section className="dashboard-section">
      <SectionHeading
        title="Give somebody the product"
        description="A code is a real Stripe coupon. Redeeming one creates a real subscription at $0, and entitlement arrives from Stripe's webhook exactly as it does for a paying customer."
      />

      <Card className="admin-promo">
        <form onSubmit={submit} className="admin-promo__form">
          <div className="admin-promo__grid">
            <label className="field">
              <span className="field__label">How many codes</span>
              <input
                className="field__input"
                type="number"
                min={1}
                max={MAX_CODES_PER_REQUEST}
                value={form.count}
                onChange={(event) => set('count', event.target.value)}
              />
              <span className="field__hint">Up to {MAX_CODES_PER_REQUEST} at a time.</span>
            </label>

            <label className="field">
              <span className="field__label">Discount</span>
              <input
                className="field__input"
                type="number"
                min={1}
                max={100}
                value={form.percent}
                onChange={(event) => set('percent', event.target.value)}
              />
              <span className="field__hint">100 means free.</span>
            </label>

            <label className="field">
              <span className="field__label">For how long</span>
              <select
                className="field__input"
                value={form.duration}
                onChange={(event) => set('duration', event.target.value)}
              >
                <option value="forever">As long as the subscription lasts</option>
                <option value="once">The first billing period only</option>
                <option value="3">Three months</option>
                <option value="6">Six months</option>
                <option value="12">Twelve months</option>
              </select>
            </label>

            <label className="field">
              <span className="field__label">Uses per code</span>
              <input
                className="field__input"
                type="number"
                min={1}
                value={form.maxRedemptions}
                onChange={(event) => set('maxRedemptions', event.target.value)}
              />
              <span className="field__hint">Prefer one code per person.</span>
            </label>

            <label className="field">
              <span className="field__label">Expires (optional)</span>
              <input
                className="field__input"
                type="date"
                value={form.expires}
                onChange={(event) => set('expires', event.target.value)}
              />
              <span className="field__hint">The last day it may be redeemed.</span>
            </label>

            <label className="field">
              <span className="field__label">Code prefix</span>
              <input
                className="field__input"
                type="text"
                value={form.prefix}
                maxLength={20}
                onChange={(event) => set('prefix', event.target.value)}
              />
            </label>
          </div>

          <fieldset className="admin-promo__plans">
            <legend className="field__label">Which plans</legend>
            <p className="field__hint">Leave both unticked for a code that works on any plan.</p>
            {(['premium', 'family'] as const).map((tier) => (
              <label key={tier} className="checkbox-field">
                <input
                  type="checkbox"
                  checked={form.tiers.includes(tier)}
                  onChange={(event) =>
                    set(
                      'tiers',
                      event.target.checked
                        ? [...form.tiers, tier]
                        : form.tiers.filter((entry) => entry !== tier),
                    )
                  }
                />
                <span>{tier === 'premium' ? 'Premium' : 'Family'}</span>
              </label>
            ))}
          </fieldset>

          <label className="field">
            <span className="field__label">Note</span>
            <input
              className="field__input"
              type="text"
              value={form.note}
              maxLength={MAX_NOTE_LENGTH}
              placeholder="Ms Alvarez, Rosewood Middle School"
              onChange={(event) => set('note', event.target.value)}
            />
            <span className="field__hint">
              Stored on the coupon and visible in Stripe. Write who it is for — you will be reading this in a year.
            </span>
          </label>

          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={form.firstTimeOnly}
              onChange={(event) => set('firstTimeOnly', event.target.checked)}
            />
            <span>Only for customers who have never paid before</span>
          </label>

          <p className="admin-promo__preview">
            {preview.ok ? (
              <>
                <strong>{describeOffer(preview.offer)}</strong>
                {' · '}
                {preview.offer.count} {preview.offer.count === 1 ? 'code' : 'codes'} ×{' '}
                {preview.offer.maxRedemptions} redemption{preview.offer.maxRedemptions === 1 ? '' : 's'}
              </>
            ) : (
              <span className="admin-promo__invalid">{preview.error}</span>
            )}
          </p>

          {/*
            A code that stops covering the bill leaves the account facing a real
            invoice, so Stripe still collects a card. That is honest behaviour
            but not what "free" usually means to the person holding the code —
            so it is said before the codes exist, not discovered at checkout.
          */}
          {preview.ok && preview.offer.percent === 100 && preview.offer.duration.kind !== 'forever' ? (
            <p className="admin-promo__warning">
              These codes stop covering the subscription when the free period ends, so checkout will still ask for a
              card. They are a discount, not a giveaway.
            </p>
          ) : null}

          {error ? <p className="form-error" role="alert">{error}</p> : null}

          <div className="button-row">
            <Button type="submit" disabled={busy || !preview.ok}>
              {busy ? 'Creating…' : 'Create the codes'}
            </Button>
            <Button variant="ghost" onClick={() => { setForm(BLANK_FORM); setMinted(null); setError(null); }}>
              Reset
            </Button>
          </div>
        </form>
      </Card>

      {minted ? (
        <Card className="admin-codes">
          <h3 className="parent-card__title">
            {minted.codes.length} {minted.codes.length === 1 ? 'code' : 'codes'} ready to share
          </h3>
          <p className="parent-card__note">
            {minted.offer} · batch <code>{minted.batch}</code>
          </p>
          {minted.warning ? <p className="admin-promo__warning">{minted.warning}</p> : null}
          {minted.needsCard ? (
            <p className="admin-promo__warning">Checkout will still ask the redeemer for a card.</p>
          ) : null}

          <ul className="admin-codes__list">
            {minted.codes.map((code) => (
              <li key={code}><code>{code}</code></li>
            ))}
          </ul>

          <div className="button-row">
            <Button variant="secondary" onClick={copyAll}>{copied ? 'Copied' : 'Copy all'}</Button>
          </div>

          <p className="page__note">
            These are shown once. Nothing on this site stores them — they live in Stripe, under
            <strong> Product catalogue → Coupons</strong>, where the batch id above finds them again. To revoke the
            whole batch, archive its coupon.
          </p>
          <p className="page__note">
            Send the person four steps, not just the code: create an account, open <Link to="/pricing">/pricing</Link>,
            choose a plan, then click <strong>Add promotion code</strong> on Stripe's page. A child account cannot
            redeem one — money is an adult's decision (§29), so give it to the guardian.
          </p>
        </Card>
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Accounts                                                                   */
/* -------------------------------------------------------------------------- */

function Accounts(): JSX.Element {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchAccounts(query, page).then((result) => {
      if (cancelled) return;
      setAccounts(result.accounts);
      setTotal(result.total);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [query, page]);

  const pages = Math.max(Math.ceil(total / ACCOUNTS_PER_PAGE), 1);

  return (
    <section className="dashboard-section">
      <SectionHeading
        title="Accounts"
        description="Who signed up, what state they are in, and whether they have been back. Not what they have been learning."
      />

      <form
        className="admin-search"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(0);
          setQuery(search);
        }}
      >
        <label className="field">
          <span className="field__label">Find by email</span>
          <input
            className="field__input"
            type="search"
            value={search}
            placeholder="someone@example.com"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <Button type="submit" variant="secondary">Search</Button>
      </form>

      <Card>
        {loading ? (
          <p>Loading…</p>
        ) : accounts.length === 0 ? (
          <p>{query ? `No account matches “${query}”.` : 'No accounts yet.'}</p>
        ) : (
          <div className="admin-table__scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Kind</th>
                  <th scope="col">Status</th>
                  <th scope="col">Plan</th>
                  <th scope="col" className="admin-table__number">Learners</th>
                  <th scope="col">Joined</th>
                  <th scope="col">Last active</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((entry) => (
                  <tr key={entry.id}>
                    <td className="admin-table__email">{entry.email}</td>
                    <td>
                      {entry.kind}
                      {/* A child with no guardian approval cannot be taught, and
                          that is the one thing on this row worth acting on. */}
                      {entry.kind === 'child' && !entry.consentGrantedAt ? (
                        <> <Badge tone="warn">no consent</Badge></>
                      ) : null}
                    </td>
                    <td>
                      <Badge tone={entry.status === 'active' ? 'correct' : entry.status === 'pending' ? 'warn' : 'incorrect'}>
                        {entry.status}
                      </Badge>
                    </td>
                    <td>
                      {entry.tier ? (
                        <>
                          {entry.tier} <Badge tone={badgeToneFor(entry.subscriptionStatus ?? '')}>
                            {(entry.subscriptionStatus ?? '').replace(/_/g, ' ')}
                          </Badge>
                        </>
                      ) : (
                        <span className="admin-table__muted">free</span>
                      )}
                    </td>
                    <td className="admin-table__number">{entry.learnerCount}</td>
                    <td>{formatDay(entry.createdAt)}</td>
                    <td>{entry.lastActiveAt ? formatDay(entry.lastActiveAt) : <span className="admin-table__muted">never</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {pages > 1 ? (
        <div className="button-row">
          <Button variant="ghost" disabled={page === 0} onClick={() => setPage((current) => current - 1)}>
            Previous
          </Button>
          <span className="page__note">Page {page + 1} of {pages} · {total} accounts</span>
          <Button variant="ghost" disabled={page + 1 >= pages} onClick={() => setPage((current) => current + 1)}>
            Next
          </Button>
        </div>
      ) : null}
    </section>
  );
}

const formatDay = (at: number): string =>
  new Date(at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
